/**
 * Lightweight Markdown-to-React renderer for LLM reasoning responses.
 *
 * Converts a subset of Markdown syntax into React elements without
 * requiring external dependencies like react-markdown. Builds a safe
 * React element tree (no raw HTML injection).
 *
 * Supported syntax:
 * - Headers (## h2, ### h3)
 * - Bold (**text**)
 * - Italic (*text*)
 * - Inline code (`code`)
 * - Fenced code blocks (```lang ... ```)
 * - Unordered lists (- item)
 * - Ordered lists (1. item)
 * - Paragraphs (blank-line separated)
 */

import React from 'react';
import {
  borderRadiusContainer,
  colorBackgroundLayoutPanelContent,
  colorBorderDividerDefault,
  fontFamilyMonospace,
  fontSizeBodyS,
  fontWeightHeadingS,
} from '@amzn/awsui-design-tokens-console';

// ---------------------------------------------------------------------------
// Inline formatting
// ---------------------------------------------------------------------------

/**
 * Parse inline Markdown formatting into React nodes.
 *
 * Handles bold, italic, and inline code within a single line of text.
 */
function parseInline(text: string): React.ReactNode[] {
  const nodes: React.ReactNode[] = [];
  // Match: **bold**, *italic*, `code`
  const pattern = /(\*\*(.+?)\*\*|\*(.+?)\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    // Text before the match
    if (match.index > lastIndex) {
      nodes.push(text.slice(lastIndex, match.index));
    }

    if (match[2] != null) {
      // Bold
      nodes.push(React.createElement('strong', { key: `b-${match.index}` }, match[2]));
    } else if (match[3] != null) {
      // Italic
      nodes.push(React.createElement('em', { key: `i-${match.index}` }, match[3]));
    } else if (match[4] != null) {
      // Inline code
      nodes.push(
        React.createElement(
          'code',
          {
            key: `c-${match.index}`,
            style: {
              backgroundColor: colorBackgroundLayoutPanelContent,
              padding: '1px 4px',
              borderRadius: '3px',
              fontFamily: fontFamilyMonospace,
              fontSize: '0.9em',
            },
          },
          match[4],
        ),
      );
    }

    lastIndex = match.index + match[0].length;
  }

  // Remaining text
  if (lastIndex < text.length) {
    nodes.push(text.slice(lastIndex));
  }

  return nodes.length > 0 ? nodes : [text];
}

// ---------------------------------------------------------------------------
// Block-level parsing
// ---------------------------------------------------------------------------

interface Block {
  type: 'heading' | 'code' | 'ul' | 'ol' | 'paragraph';
  level?: number; // heading level
  lang?: string; // code language
  content: string; // raw text content
  items?: string[]; // list items
}

/**
 * Split raw Markdown text into block-level structures.
 */
function parseBlocks(markdown: string): Block[] {
  const lines = markdown.split('\n');
  const blocks: Block[] = [];
  let i = 0;

  while (i < lines.length) {
    const line = lines[i];

    // Blank line -- skip
    if (line.trim() === '') {
      i++;
      continue;
    }

    // Fenced code block
    const codeMatch = line.match(/^```(\w*)/);
    if (codeMatch) {
      const lang = codeMatch[1] || undefined;
      const codeLines: string[] = [];
      i++;
      while (i < lines.length && !lines[i].startsWith('```')) {
        codeLines.push(lines[i]);
        i++;
      }
      if (i < lines.length) i++; // skip closing ```
      blocks.push({ type: 'code', lang, content: codeLines.join('\n') });
      continue;
    }

    // Heading
    const headingMatch = line.match(/^(#{1,4})\s+(.+)$/);
    if (headingMatch) {
      blocks.push({
        type: 'heading',
        level: headingMatch[1].length,
        content: headingMatch[2],
      });
      i++;
      continue;
    }

    // Unordered list
    if (/^[-*]\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^[-*]\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^[-*]\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ul', content: '', items });
      continue;
    }

    // Ordered list
    if (/^\d+\.\s+/.test(line)) {
      const items: string[] = [];
      while (i < lines.length && /^\d+\.\s+/.test(lines[i])) {
        items.push(lines[i].replace(/^\d+\.\s+/, ''));
        i++;
      }
      blocks.push({ type: 'ol', content: '', items });
      continue;
    }

    // Paragraph -- collect consecutive non-blank, non-special lines
    const paraLines: string[] = [];
    while (
      i < lines.length &&
      lines[i].trim() !== '' &&
      !lines[i].match(/^```/) &&
      !lines[i].match(/^#{1,4}\s+/) &&
      !lines[i].match(/^[-*]\s+/) &&
      !lines[i].match(/^\d+\.\s+/)
    ) {
      paraLines.push(lines[i]);
      i++;
    }
    if (paraLines.length > 0) {
      blocks.push({ type: 'paragraph', content: paraLines.join('\n') });
    }
  }

  return blocks;
}

// ---------------------------------------------------------------------------
// Render blocks to React elements
// ---------------------------------------------------------------------------

/**
 * Render parsed blocks into an array of React elements.
 */
function renderBlocks(blocks: Block[]): React.ReactNode[] {
  return blocks.map((block, idx) => {
    const key = `md-${idx}`;

    switch (block.type) {
      case 'heading': {
        const tag = `h${Math.min(block.level ?? 3, 6)}` as keyof React.JSX.IntrinsicElements;
        return React.createElement(
          tag,
          {
            key,
            style: {
              margin: '0.6em 0 0.3em',
              fontSize: block.level === 1 ? '1.2em' : block.level === 2 ? '1.1em' : '1em',
              fontWeight: fontWeightHeadingS,
            },
          },
          ...parseInline(block.content),
        );
      }

      case 'code':
        return React.createElement(
          'pre',
          {
            key,
            style: {
              backgroundColor: colorBackgroundLayoutPanelContent,
              border: `1px solid ${colorBorderDividerDefault}`,
              borderRadius: borderRadiusContainer,
              padding: '10px 12px',
              fontFamily: fontFamilyMonospace,
              fontSize: fontSizeBodyS,
              whiteSpace: 'pre-wrap',
              overflowX: 'auto',
              margin: '0.4em 0',
            },
          },
          React.createElement('code', null, block.content),
        );

      case 'ul':
        return React.createElement(
          'ul',
          { key, style: { margin: '0.4em 0', paddingLeft: '1.4em' } },
          (block.items ?? []).map((item, li) =>
            React.createElement('li', { key: `${key}-${li}` }, ...parseInline(item)),
          ),
        );

      case 'ol':
        return React.createElement(
          'ol',
          { key, style: { margin: '0.4em 0', paddingLeft: '1.4em' } },
          (block.items ?? []).map((item, li) =>
            React.createElement('li', { key: `${key}-${li}` }, ...parseInline(item)),
          ),
        );

      case 'paragraph':
      default:
        return React.createElement(
          'p',
          { key, style: { margin: '0.4em 0', lineHeight: 1.5 } },
          ...parseInline(block.content),
        );
    }
  });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Render a Markdown string as React elements.
 *
 * Returns a div wrapping the rendered block elements.
 * Safe by design: builds React element tree, no raw HTML injection.
 */
export function SimpleMarkdown({ content }: { content: string }): React.JSX.Element {
  const blocks = parseBlocks(content);
  return React.createElement('div', null, ...renderBlocks(blocks));
}
